import {
	ErrorModal,
	getMomentTz,
	AssetTreePicker,
	useToken,
	useUserContext,
} from "@dexteel/mesf-core";
import { isNil, isNumber } from "lodash-es";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
	Button,
	Divider,
	IconButton,
	Paper,
	Tooltip,
	Typography,
} from "@mui/material";
import Grid2 from '@mui/material/Grid2';
import type {
	ColDef,
	DefaultMenuItem,
	EditableCallbackParams,
	GetContextMenuItemsParams,
	GetRowIdFunc,
	GetRowIdParams,
	GridApi,
	GridReadyEvent,
	ICellRendererParams,
	MenuItemDef,
	RowDragEndEvent,
	RowDropZoneParams,
	SelectionChangedEvent,
} from "ag-grid-community";
import { DeleteWorkOrderModal } from "@/shared/work-orders-modals/components/modal-delete-work-order";
import { EditWorkOrderModal } from "@/shared/work-orders-modals/components/modal-edit-work-order";
import { useTaskListContext } from "../context/taskListContext";
import { Filters } from "../filters";
import { useGetDataFromAPI } from "../hooks/useGetDataFromAPI";
import { useInitializeTaskList } from "../hooks/useInitializeTaskList";
import { useQueryClient } from "@tanstack/react-query";
import {
	TASK_LIST_QUERY_KEYS,
	useTaskList,
} from "../hooks/useTaskListQueries";
import { useUpdateTaskListPriority } from "../hooks/useTaskListMutations";
import { DownDayPMsPicker } from "./components/DownDayPMsPicker";
import FormatListBulletedSharpIcon from "@mui/icons-material/FormatListBulletedSharp";
import AddBoxIcon from "@mui/icons-material/AddBox";
import ScheduleIcon from "@mui/icons-material/Schedule";
import { AgGridReact } from "ag-grid-react";
import CustomLoadingOverlay from "../../../components/loaders/LazyLoading/LazyLoading";
import { AssetNameColumnColumn } from "../components/assetName-column";
import { CloseWorkOrderModalWithAttachments } from "@/shared/work-orders-modals/components/modal-close-work-order";
import type { ModalWorkOrder } from "@/shared/work-orders-modals/models/ModalWorkOrder";
import { globalDefaultColDef } from "@setup/ag-grid/mesf-theme";
import { ExcelButton } from "@/components/buttons/excel-button";
import { PdfButton } from "@/components/buttons/pdf-button";
import { openTaskListPdf, openWorkOrderPdf } from "@/shared/work-order-pdf";

type onMenuOptionSelectedType =
	| "EDIT"
	| "EDIT-COMMENTS"
	| "SEE-COMMENTS"
	| "CLOSE-ORDER"
	| "ADD-ORDER"
	| "DELETE-ORDER";

const moment = getMomentTz();

// Task-list list the dragged order is dropped into. Mirrors @TargetCategory in
// MMS.UpdateTaskListPriority ('T' Down Time, 'N' Next Down Day, 'D' Down Day, 'R' Routine).
const CATEGORY = {
	DOWN_TIME: "T",
	NEXT_DOWN_DAY: "N",
	DOWN_DAY: "D",
	ROUTINE: "R",
} as const;

// Column keys written to the task-list Excel export. Comments (and Down Hours on the grids
// that do not display it) live in hidden "phantom" columns so they reach the file without
// being shown in the grid.
const TASK_LIST_EXPORT_COLUMN_KEYS = [
	"AssetName",
	"Title",
	"DownHours",
	"CraftId",
	"TypeCode",
	"Comments",
];

// Export a whole task-list grid to Excel (used by each list's export button).
// Requires the AG Grid Enterprise Excel export.
const exportTaskListToExcel = (api: GridApi, fileName = "TaskList.xlsx") => {
	api.exportDataAsExcel({
		fileName,
		columnKeys: TASK_LIST_EXPORT_COLUMN_KEYS,
	});
};

// Export a single task-list row to Excel (used by the row right-click menu). The
// right-clicked order is exported regardless of the current selection.
const exportTaskListRowToExcel = (
	api: GridApi,
	workOrderId: number,
	fileName = "TaskList.xlsx",
) => {
	api.exportDataAsExcel({
		fileName,
		columnKeys: TASK_LIST_EXPORT_COLUMN_KEYS,
		shouldRowBeSkipped: (p) => p.node?.data?.WorkOrderId !== workOrderId,
	});
};

// Print a whole task-list grid: the same rows, in the same order and under the same filters
// the user is looking at. Rendered server-side as a single table by /api/work-orders/pdf-list.
const exportTaskListToPdf = (
	api: GridApi,
	listTitle: string,
	sectionAssetId: number,
) => {
	const workOrderIds: number[] = [];
	api.forEachNodeAfterFilterAndSort((node) => {
		if (node.data?.WorkOrderId) workOrderIds.push(node.data.WorkOrderId);
	});
	openTaskListPdf(workOrderIds, listTitle, sectionAssetId);
};

export const TaskListByDownHours = () => {
	const {
		state: {
			selectedRow,
			assetSearchId,
			craftSearchId,
			typeCode,
			searchText,
			downHours,
			openWorkOrderEditModal,
			openCloseWorkOrderModal,
			openDeleteWorkOrderModal,
		},
		actions: {
			setSelectedRow,
			setOpenWorkOrderEditModal,
			setOpenCommentsModal,
			setOpenCloseWorkOrderModal,
			setOpenDeleteWorkOrderModal,
			setAssetSearchId,
		},
	} = useTaskListContext();
	const {
		state: { defaultAreaId },
	} = useUserContext();
	const [error, setError] = useState<string>("");
	const [taskListError, setTaskListError] = useState<string>("");
	const [newMode, setNewMode] = useState(false);
	// null = picker closed; 'D' / 'N' = open for that down-day code.
	const [pickerCode, setPickerCode] = useState<string | null>(null);
	const { getUserName } = useToken();

	const gridRef1 = useRef<AgGridReact>(null);
	const gridRef2 = useRef<AgGridReact>(null);
	const gridRef3 = useRef<AgGridReact>(null);
	const gridRef4 = useRef<AgGridReact>(null);
	const gridStyle = useMemo(() => ({ height: "88%", width: "100%", paddingTop: "50px" }), []);

	const [areaApi, setAreaApi] = useState<{ [key: number]: GridApi } | null>(
		null,
	);
	const [insertTaskError, setInsertTaskError] = useState<string>("");
	const [updatePriorityError, setUpdatePriorityError] = useState<string>("");

	const {
		tasks,
		isLoading: isTaskListLoading,
		error: listError,
	} = useTaskList({
		craftSearchId,
		typeCode,
		searchText,
		downHours,
		assetSearchId,
	});
	const queryClient = useQueryClient();
	const refreshTaskList = useCallback(
		() => queryClient.invalidateQueries({ queryKey: TASK_LIST_QUERY_KEYS.all }),
		[queryClient],
	);
	const { updatePriority } = useUpdateTaskListPriority({
		onError: (e) => setUpdatePriorityError(e.message),
	});
	const updatePriorityRef = useRef(updatePriority);
	updatePriorityRef.current = updatePriority;

	useEffect(() => {
		if (listError) setTaskListError(listError.message);
	}, [listError]);

	const [areasNumber, setAreasNumber] = useState(3);

	const onActionsClick = (
		e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
		params: EditableCallbackParams,
	) => {
		(params.api as any).showContextMenu({
			column: params.column,
			rowNode: params.node,
			mouseEvent: e.nativeEvent,
		});
	};

	const loadingOverlayComponent = useMemo<any>(() => {
		return CustomLoadingOverlay;
	}, []);

	// Builds a cross-grid drop zone over one column's container. When an order is
	// dropped here, it is moved into that column's category (TargetCategory) and
	// re-prioritized below the row it was dropped over.
	const buildDropZone = useCallback(
		(
			containerId: string,
			getTargetApi: () => GridApi | null | undefined,
			category: string,
		): RowDropZoneParams => ({
			getContainer: () => document.getElementById(containerId)!,
			onDragStop: async (params) => {
				const target = getTargetApi();
				if (!params?.api || !target) return;

				try {
					const draggingNodes = params.nodes;
					if (!draggingNodes || draggingNodes.length === 0) return;

					const draggedNodeData = draggingNodes[0]?.data;
					if (!draggedNodeData?.WorkOrderId) return;

					const dragItemId = draggedNodeData.WorkOrderId;
					let workOrderIdBelow = null;
					const overNode = params.overNode;

					if (overNode) {
						if (overNode.data?.WorkOrderId !== dragItemId) {
							workOrderIdBelow = overNode.data.WorkOrderId;
						} else if (
							params.overIndex !== undefined &&
							params.overIndex> 0
						) {
							const rowBeforeDrop = target.getDisplayedRowAtIndex(
								params.overIndex - 1,
							);
							if (
								rowBeforeDrop &&
								rowBeforeDrop.data?.WorkOrderId !== dragItemId
							) {
								workOrderIdBelow = rowBeforeDrop.data.WorkOrderId;
							}
						}
					} else if (target.getDisplayedRowCount()> 0) {
						const lastRowNode = target.getDisplayedRowAtIndex(
							target.getDisplayedRowCount() - 1,
						);
						if (
							lastRowNode &&
							lastRowNode.data?.WorkOrderId !== dragItemId
						) {
							workOrderIdBelow = lastRowNode.data.WorkOrderId;
						}
					}

					updatePriorityRef.current({
						WorkOrderIdToMove: dragItemId,
						WorkOrderIdMoveBelow: workOrderIdBelow,
						TargetCategory: category,
					});
				} finally {
					params.api.hideOverlay();
					target.hideOverlay();
				}
			},
		}),
		[],
	);

	// Registers every list's drop zone on a source grid so its rows can be dragged
	// into any list. In-list reordering is handled by onRowDragEnd.
	const registerDropZones = useCallback(
		(sourceApi: GridApi | null) => {
			if (!sourceApi?.addRowDropZone) return;
			sourceApi.addRowDropZone(
				buildDropZone(
					"drop-zone-downtime",
					() => gridRef1.current?.api,
					CATEGORY.DOWN_TIME,
				),
			);
			sourceApi.addRowDropZone(
				buildDropZone(
					"drop-zone-nextdownday",
					() => gridRef4.current?.api,
					CATEGORY.NEXT_DOWN_DAY,
				),
			);
			sourceApi.addRowDropZone(
				buildDropZone(
					"drop-zone-downday",
					() => gridRef3.current?.api,
					CATEGORY.DOWN_DAY,
				),
			);
			sourceApi.addRowDropZone(
				buildDropZone(
					"drop-zone-routine",
					() => gridRef2.current?.api,
					CATEGORY.ROUTINE,
				),
			);
		},
		[buildDropZone],
	);

	// The asset the four lists are filtered by. Sent as an id, not a name: the page has no
	// reliable asset name to hand (the mesf-core asset context is only filled by the Assets Tree
	// screen), so the server resolves it.
	const sectionAssetId = Number(assetSearchId ?? defaultAreaId ?? 0);

	// Split into the four lists. DownDayCode wins over DownHours: a down-day order
	// ('N' or 'D') always lands in its down-day list regardless of inherited down hours.
	const { routineRows, downHourRows, downDayRows, nextDownDayRows } = useMemo(
		() =>
			(tasks || []).reduce(
				(carry, current) => {
					if (!current) return carry;

					if (current.DownDayCode === "N") {
						carry.nextDownDayRows.push(current);
					} else if (current.DownDayCode === "D") {
						carry.downDayRows.push(current);
					} else if (isNumber(current.DownHours) && current.DownHours> 0) {
						carry.downHourRows.push(current);
					} else {
						carry.routineRows.push(current);
					}
					return carry;
				},
				{
					routineRows: [] as any[],
					downHourRows: [] as any[],
					downDayRows: [] as any[],
					nextDownDayRows: [] as any[],
				},
			),
		[tasks],
	);

	const onRowDragEnd = async (params: RowDragEndEvent) => {
		if (!params?.node?.data?.WorkOrderId || !params?.api) return;

		const draggedNodeData = params.node.data;
		const draggedNodeId = draggedNodeData.WorkOrderId;

		let workOrderIdBelow = null;
		if (params.overIndex !== undefined && params.overIndex !== null) {
			const overNode = params.api.getDisplayedRowAtIndex(params.overIndex);
			if (overNode && overNode.data?.WorkOrderId !== draggedNodeId) {
				workOrderIdBelow = overNode.data.WorkOrderId;
			} else if (params.overIndex> 0) {
				const rowBeforeDrop = params.api.getDisplayedRowAtIndex(
					params.overIndex - 1,
				);
				if (
					rowBeforeDrop &&
					rowBeforeDrop.data?.WorkOrderId !== draggedNodeId
				) {
					workOrderIdBelow = rowBeforeDrop.data.WorkOrderId;
				}
			}
		}
		try {
			// In-grid reorder does not change the list category, so leave both
			// DownHours and DownDayCode untouched (null = no category change).
			updatePriority({
				WorkOrderIdToMove: draggedNodeId,
				WorkOrderIdMoveBelow: workOrderIdBelow,
				TargetCategory: null,
			});
		} finally {
			params.api?.hideOverlay?.();
		}
	};

	const onGridReadyForArea = (area: number, params: GridReadyEvent) => {
		if (!params.api) return;

		setAreaApi((prev) => ({ ...(prev || {}), [area]: params.api }));
	};

	// Keep a single active selection across the four lists: selecting a row in one
	// grid clears the highlighted selection in the other three.
	const handleSelectionChanged = useCallback((event: SelectionChangedEvent) => {
		if (event.api.getSelectedNodes().length === 0) return;
		for (const ref of [gridRef1, gridRef2, gridRef3, gridRef4]) {
			const api = ref.current?.api;
			if (api && api !== event.api) api.deselectAll();
		}
	}, []);

	const renderCategoryCell = (props: ICellRendererParams) => {
		if (!props?.data) return null;

		return (
			<div
				style={{
					height: 32,
					display: "flex",
					justifyContent: "center",
					alignItems: "center",
				}}>
				<div
					style={{
						height: 26,
						width: 26,
						borderRadius: 4,
						display: "flex",
						justifyContent: "center",
						alignItems: "center",
						background: props.data?.CraftColor || "#FFFFFF",
					}}>
					<div style={{ color: "#FFF", fontWeight: 600 }}>
						<div>{props.data?.CraftName?.charAt?.(0) || ""}</div>
					</div>
				</div>
			</div>
		);
	};

	const renderTypeCell = (props: ICellRendererParams) => {
		if (!props.data) return null;

		return (
			<div
				style={{
					height: 32,
					display: "flex",
					justifyContent: "center",
					alignItems: "center",
				}}>
				<div
					style={{
						height: 26,
						width: 26,
						borderRadius: 4,
						display: "flex",
						justifyContent: "center",
						alignItems: "center",
						background: props.data?.TypeCode == "PM" ? "#00008B" : "#D3D3D3",
					}}>
					<div
						style={{
							color: props.data?.TypeCode == "PM" ? "#FFFFFF" : "#000000",
							fontWeight: 600,
						}}>
						<div>{props.data?.TypeCode || ""}</div>
					</div>
				</div>
			</div>
		);
	};

	const [downHourColumnDefs, setDownHourColumnDefs] = useState<ColDef[]>([
		// Hidden "phantom" column: not shown in the grid, exported to Excel.
		{ field: "Comments", headerName: "Comments", hide: true },
		{ field: "", headerName: "", rowDrag: true, minWidth: 20, maxWidth: 20 },
		{
			field: "AssetName",
			cellRenderer: AssetNameColumnColumn,
			headerName: "Asset",
			minWidth: 90,
			flex: 4,
		},
		{ field: "Title", headerName: "Title", minWidth: 90, flex: 5 },
		{
			field: "DownHours",
			headerName: "Down Hours",
			minWidth: 60,
			maxWidth: 60,
			flex: 1,
		},
		{
			field: "CraftId",
			headerName: "Craft",
			minWidth: 35,
			maxWidth: 35,
			flex: 1,
			cellRenderer: renderCategoryCell,
		},
		{
			field: "TypeCode",
			headerName: "Type",
			minWidth: 55,
			maxWidth: 55,
			flex: 1,
			cellRenderer: renderTypeCell,
		},
		{
			headerName: "",
			flex: 1,
			minWidth: 65,
			maxWidth: 65,
			cellClass: "ag-columns",
			wrapText: false,
			autoHeight: false,
			cellRenderer: (params: EditableCallbackParams) => {
				return (
					<Grid2 container
						style={{
							height: "100%",
							display: "flex",
							justifyContent: "center",
							alignItems: "center",
						}}>
						<Grid2 size={{ xs: 12, md: 12 }} style={{ display: "flex", justifyContent: "center", alignItems: "center", }}>
							<Grid2 spacing={1}
								style={{ justifyContent: "flex-end", minWidth: 30 }}>
								<Grid2 size={{ xs: 12, md: 12 }}>
									<Button
										fullWidth
										style={{}}
										onClick={(e) => onActionsClick(e, params)}>
										<FormatListBulletedSharpIcon
											style={{ height: "auto" }}
											color="action" />
									</Button>
								</Grid2>
							</Grid2>
						</Grid2>
					</Grid2>
				);
			},
		},
	]);

	const [routineColumnDefs, setRoutineColumnDefs] = useState<ColDef[]>([
		// Hidden "phantom" columns: not shown in the grid, exported to Excel.
		{ field: "Comments", headerName: "Comments", hide: true },
		{ field: "DownHours", headerName: "Down Hours", hide: true },
		{ field: "", headerName: "", rowDrag: true, minWidth: 20, maxWidth: 20 },
		{
			field: "AssetName",
			cellRenderer: AssetNameColumnColumn,
			headerName: "Asset",
			minWidth: 90,
			flex: 5,
		},
		{ field: "Title", headerName: "Title", minWidth: 90, flex: 5 },
		{
			field: "CraftId",
			headerName: "Craft",
			minWidth: 35,
			maxWidth: 35,
			flex: 1,
			cellRenderer: renderCategoryCell,
		},
		{
			field: "TypeCode",
			headerName: "Type",
			minWidth: 55,
			maxWidth: 55,
			flex: 1,
			cellRenderer: renderTypeCell,
		},
		{
			headerName: "",
			flex: 1,
			minWidth: 65,
			maxWidth: 65,
			cellClass: "ag-columns",
			wrapText: false,
			autoHeight: false,
			cellRenderer: (params: EditableCallbackParams) => {
				return (
					<Grid2 container
						style={{
							height: "100%",
							display: "flex",
							justifyContent: "center",
							alignItems: "center",
						}}>
						<Grid2 size={{ xs: 12, md: 12 }} style={{ display: "flex", justifyContent: "center", alignItems: "center", }}>
							<Grid2 spacing={1}
								style={{ justifyContent: "flex-end", minWidth: 30 }}>
								<Grid2 size={{ xs: 12, md: 12 }}>
									<Button
										fullWidth
										style={{}}
										onClick={(e) => onActionsClick(e, params)}>
										<FormatListBulletedSharpIcon
											style={{ height: "auto" }}
											color="action" />
									</Button>
								</Grid2>
							</Grid2>
						</Grid2>
					</Grid2>
				);
			},
		},
	]);

	// The Down Day grid must NOT share the Routine grid's column-definition array:
	// AG Grid keeps per-grid row-drag state on the colDef objects, so two grids
	// pointing at the same instances corrupt each other's drag. Give it its own copy.
	const downDayColumnDefs = useMemo<ColDef[]>(
		() => routineColumnDefs.map((col) => ({ ...col })),
		[routineColumnDefs],
	);

	// Each grid needs its own column-definition objects (AG Grid keeps per-grid
	// row-drag state on them); the Next Down Day grid gets its own copy too.
	const nextDownDayColumnDefs = useMemo<ColDef[]>(
		() => routineColumnDefs.map((col) => ({ ...col })),
		[routineColumnDefs],
	);

	const onMenuOptionSelected = useCallback(
		(
			option: onMenuOptionSelectedType,
			row: ModalWorkOrder,
			areaAssetId: number | null = null,
		) => {
			if (!row) return;

			setSelectedRow(row);
			switch (option) {
				case "EDIT": {
					setOpenWorkOrderEditModal(true);
					return;
				}
				case "EDIT-COMMENTS": {
					setOpenCommentsModal(true);
					return;
				}
				case "SEE-COMMENTS": {
					setOpenCommentsModal(true);
					return;
				}
				case "CLOSE-ORDER": {
					setOpenCloseWorkOrderModal(true);
					return;
				}
				case "ADD-ORDER": {
					const targetAssetId =
						areaAssetId !== null
							? areaAssetId
							: row?.AssetId !== null
								? row.AssetId
								: defaultAreaId;
					const isDownTime = isNumber(row?.DownHours) && row.DownHours> 0;
					addNewOrder(targetAssetId as number, isDownTime);
					return;
				}
				case "DELETE-ORDER": {
					setOpenDeleteWorkOrderModal(true);
					return;
				}
				default:
					return;
			}
		},
		[
			setSelectedRow,
			setOpenWorkOrderEditModal,
			setOpenCommentsModal,
			setOpenCloseWorkOrderModal,
			setOpenDeleteWorkOrderModal,
			defaultAreaId,
		],
	);

	const getContextMenuItemsForDownHours = useCallback(
		(params: GetContextMenuItemsParams): (DefaultMenuItem | MenuItemDef)[] => {
			if (!params.node?.data) {
				return [
					{
						name: "Add Down Time Order",
						action: () => {
							const targetAssetId = assetSearchId ?? defaultAreaId ?? 0;
							const tempWorkOrder = {
								AssetId: targetAssetId,
								AssetName: "",
								// Default to 1 down hour so the order lands in Down Time, not Routine.
								DownHours: 1,
								WorkOrderId: null,
								Title: "",
								CraftId: 1,
								TypeCode: "RM",
								WorkOrderStatusCode: "O",
								WorkOrderName: "",
								WorkOrderNumberOfWorkers: null,
								OpenTimestamp: moment(new Date()).format("MM/DD/YYYY HH:mm"),
								WorkOrderPlannedHours: null,
								WorkOrderOpenedBy: getUserName(),
							};
							setNewMode(true);
							setSelectedRow(tempWorkOrder);
							setOpenWorkOrderEditModal(true);
						},
						icon: '<i class="fas fa-plus"></i>',
					},
					"separator",
					{
						name: "Export PDF List",
						action: () =>
							exportTaskListToPdf(params.api, "Down Time Orders", sectionAssetId),
						icon: '<i class="fas fa-file-pdf"></i>',
					},
				];
			}

			const status = params.node.data.WorkOrderStatusCode;

			const result: (DefaultMenuItem | MenuItemDef)[] = [
				{
					name: "Edit Order",
					action: () => {
						onMenuOptionSelected("EDIT", params.node?.data);
					},
					icon: '<i class="fas fa-edit"></i>',
				},
			];

			result.push({
				name: "New Down Time Order",
				action: () => {
					const targetAssetId = !isNil(assetSearchId) ? assetSearchId : (defaultAreaId || 1);
					const tempWorkOrder = {
						AssetId: targetAssetId,
						AssetName: targetAssetId === 0 ? "Plant" : "",
						DownHours: isNumber(downHours) && downHours> 0 ? downHours : 1,
						WorkOrderId: null,
						Title: "",
						CraftId: 1,
						TypeCode: "RM",
						WorkOrderStatusCode: "O",
						WorkOrderName: "",
						WorkOrderNumberOfWorkers: null,
						OpenTimestamp: moment(new Date()).format("MM/DD/YYYY HH:mm"),
						WorkOrderPlannedHours: null,
						WorkOrderOpenedBy: getUserName(),
					};
					setNewMode(true);
					setSelectedRow(tempWorkOrder);
					setOpenWorkOrderEditModal(true);
				},
				icon: '<i class="fas fa-plus"></i>',
			});

			if (status === "O") {
				result.push({
					name: "Close Order",
					action: () => {
						onMenuOptionSelected("CLOSE-ORDER", params.node?.data);
					},
					icon: '<i class="far fa-times-circle"></i>',
				});
			}
			result.push(
				{
					name: "Delete Order",
					action: () => {
						onMenuOptionSelected("DELETE-ORDER", params.node?.data);
					},
					icon: '<i class="fas fa-trash"></i>',
				},
				"separator",
				{
					name: "Export to Excel",
					action: () =>
						exportTaskListRowToExcel(params.api, params.node?.data?.WorkOrderId),
					icon: '<i class="fas fa-file-excel"></i>',
				},
				{
					name: "Generate Work Order PDF",
					action: () => openWorkOrderPdf([params.node?.data?.WorkOrderId]),
					icon: '<i class="fas fa-file-pdf"></i>',
				},
				{
					name: "Export PDF List",
					action: () =>
						exportTaskListToPdf(params.api, "Down Time Orders", sectionAssetId),
					icon: '<i class="fas fa-file-pdf"></i>',
				},
			);
			return result;
		},
		[assetSearchId, defaultAreaId, downHours, getUserName, setNewMode, setSelectedRow, setOpenWorkOrderEditModal, onMenuOptionSelected, sectionAssetId],
	);

	const getContextMenuItemsForRoutine = useCallback(
		(params: GetContextMenuItemsParams): (DefaultMenuItem | MenuItemDef)[] => {
			if (!params.node?.data) {
				return [
					{
						name: "Add Routine Order",
						action: () => {
							const targetAssetId = assetSearchId ?? defaultAreaId ?? 0;
							const tempWorkOrder = {
								AssetId: targetAssetId,
								AssetName: "",
								// Routine orders default to 0 down hours, field read-only at creation.
								DownHours: 0,
								DownHoursReadOnly: true,
								WorkOrderId: null,
								Title: "",
								CraftId: 1,
								TypeCode: "RM",
								WorkOrderStatusCode: "O",
								WorkOrderName: "",
								WorkOrderNumberOfWorkers: null,
								OpenTimestamp: moment(new Date()).format("MM/DD/YYYY HH:mm"),
								WorkOrderPlannedHours: null,
								WorkOrderOpenedBy: getUserName(),
							};
							setNewMode(true);
							setSelectedRow(tempWorkOrder);
							setOpenWorkOrderEditModal(true);
						},
						icon: '<i class="fas fa-plus"></i>',
					},
					"separator",
					{
						name: "Export PDF List",
						action: () =>
							exportTaskListToPdf(params.api, "Routine Orders", sectionAssetId),
						icon: '<i class="fas fa-file-pdf"></i>',
					},
				];
			}

			const status = params.node.data.WorkOrderStatusCode;

			const result: (DefaultMenuItem | MenuItemDef)[] = [
				{
					name: "Edit Order",
					action: () => {
						onMenuOptionSelected("EDIT", params.node?.data);
					},
					icon: '<i class="fas fa-edit"></i>',
				},
			];

			result.push({
				name: "New Routine Order",
				action: () => {
					const targetAssetId = assetSearchId ?? defaultAreaId ?? 0;
					const tempWorkOrder = {
						AssetId: targetAssetId,
						AssetName: "",
						// Routine orders default to 0 down hours, field read-only at creation.
						DownHours: 0,
						DownHoursReadOnly: true,
						WorkOrderId: null,
						Title: "",
						CraftId: 1,
						TypeCode: "RM",
						WorkOrderStatusCode: "O",
						WorkOrderName: "",
						WorkOrderNumberOfWorkers: null,
						OpenTimestamp: moment(new Date()).format("MM/DD/YYYY HH:mm"),
						WorkOrderPlannedHours: null,
						WorkOrderOpenedBy: getUserName(),
					};
					setNewMode(true);
					setSelectedRow(tempWorkOrder);
					setOpenWorkOrderEditModal(true);
				},
				icon: '<i class="fas fa-plus"></i>',
			});

			if (status === "O") {
				result.push({
					name: "Close Order",
					action: () => {
						onMenuOptionSelected("CLOSE-ORDER", params.node?.data);
					},
					icon: '<i class="far fa-times-circle"></i>',
				});
			}
			result.push(
				{
					name: "Delete Order",
					action: () => {
						onMenuOptionSelected("DELETE-ORDER", params.node?.data);
					},
					icon: '<i class="fas fa-trash"></i>',
				},
				"separator",
				{
					name: "Export to Excel",
					action: () =>
						exportTaskListRowToExcel(params.api, params.node?.data?.WorkOrderId),
					icon: '<i class="fas fa-file-excel"></i>',
				},
				{
					name: "Generate Work Order PDF",
					action: () => openWorkOrderPdf([params.node?.data?.WorkOrderId]),
					icon: '<i class="fas fa-file-pdf"></i>',
				},
				{
					name: "Export PDF List",
					action: () =>
						exportTaskListToPdf(params.api, "Routine Orders", sectionAssetId),
					icon: '<i class="fas fa-file-pdf"></i>',
				},
			);
			return result;
		},
		[assetSearchId, defaultAreaId, getUserName, setNewMode, setSelectedRow, setOpenWorkOrderEditModal, onMenuOptionSelected, sectionAssetId],
	);

	const getContextMenuItemsForDownDay = useCallback(
		(params: GetContextMenuItemsParams): (DefaultMenuItem | MenuItemDef)[] => {
			// New down-day orders carry DownDayCode 'D' so they land in this list.
			const buildDownDayOrder = () => ({
				AssetId: assetSearchId ?? defaultAreaId ?? 0,
				AssetName: "",
				DownHours: null,
				DownDayCode: "D",
				WorkOrderId: null,
				Title: "",
				CraftId: 1,
				TypeCode: "RM",
				WorkOrderStatusCode: "O",
				WorkOrderName: "",
				WorkOrderNumberOfWorkers: null,
				OpenTimestamp: moment(new Date()).format("MM/DD/YYYY HH:mm"),
				WorkOrderPlannedHours: null,
				WorkOrderOpenedBy: getUserName(),
			});

			const openNewDownDayOrder = () => {
				setNewMode(true);
				setSelectedRow(buildDownDayOrder());
				setOpenWorkOrderEditModal(true);
			};

			if (!params.node?.data) {
				return [
					{
						name: "Add Down Day Order",
						action: openNewDownDayOrder,
						icon: '<i class="fas fa-plus"></i>',
					},
					"separator",
					{
						name: "Export PDF List",
						action: () =>
							exportTaskListToPdf(params.api, "Down Day Orders", sectionAssetId),
						icon: '<i class="fas fa-file-pdf"></i>',
					},
				];
			}

			const status = params.node.data.WorkOrderStatusCode;

			const result: (DefaultMenuItem | MenuItemDef)[] = [
				{
					name: "Edit Order",
					action: () => {
						onMenuOptionSelected("EDIT", params.node?.data);
					},
					icon: '<i class="fas fa-edit"></i>',
				},
				{
					name: "New Down Day Order",
					action: openNewDownDayOrder,
					icon: '<i class="fas fa-plus"></i>',
				},
			];

			if (status === "O") {
				result.push({
					name: "Close Order",
					action: () => {
						onMenuOptionSelected("CLOSE-ORDER", params.node?.data);
					},
					icon: '<i class="far fa-times-circle"></i>',
				});
			}
			result.push(
				{
					name: "Delete Order",
					action: () => {
						onMenuOptionSelected("DELETE-ORDER", params.node?.data);
					},
					icon: '<i class="fas fa-trash"></i>',
				},
				"separator",
				{
					name: "Export to Excel",
					action: () =>
						exportTaskListRowToExcel(params.api, params.node?.data?.WorkOrderId),
					icon: '<i class="fas fa-file-excel"></i>',
				},
				{
					name: "Generate Work Order PDF",
					action: () => openWorkOrderPdf([params.node?.data?.WorkOrderId]),
					icon: '<i class="fas fa-file-pdf"></i>',
				},
				{
					name: "Export PDF List",
					action: () =>
						exportTaskListToPdf(params.api, "Down Day Orders", sectionAssetId),
					icon: '<i class="fas fa-file-pdf"></i>',
				},
			);
			return result;
		},
		[assetSearchId, defaultAreaId, getUserName, setNewMode, setSelectedRow, setOpenWorkOrderEditModal, onMenuOptionSelected, sectionAssetId],
	);

	const getContextMenuItemsForNextDownDay = useCallback(
		(params: GetContextMenuItemsParams): (DefaultMenuItem | MenuItemDef)[] => {
			// New next-down-day orders carry DownDayCode 'N' so they land in this list.
			const buildNextDownDayOrder = () => ({
				AssetId: assetSearchId ?? defaultAreaId ?? 0,
				AssetName: "",
				DownHours: null,
				DownDayCode: "N",
				WorkOrderId: null,
				Title: "",
				CraftId: 1,
				TypeCode: "RM",
				WorkOrderStatusCode: "O",
				WorkOrderName: "",
				WorkOrderNumberOfWorkers: null,
				OpenTimestamp: moment(new Date()).format("MM/DD/YYYY HH:mm"),
				WorkOrderPlannedHours: null,
				WorkOrderOpenedBy: getUserName(),
			});

			const openNewNextDownDayOrder = () => {
				setNewMode(true);
				setSelectedRow(buildNextDownDayOrder());
				setOpenWorkOrderEditModal(true);
			};

			if (!params.node?.data) {
				return [
					{
						name: "Add Next Down Day Order",
						action: openNewNextDownDayOrder,
						icon: '<i class="fas fa-plus"></i>',
					},
					"separator",
					{
						name: "Export PDF List",
						action: () =>
							exportTaskListToPdf(params.api, "Next Down Day Orders", sectionAssetId),
						icon: '<i class="fas fa-file-pdf"></i>',
					},
				];
			}

			const status = params.node.data.WorkOrderStatusCode;

			const result: (DefaultMenuItem | MenuItemDef)[] = [
				{
					name: "Edit Order",
					action: () => {
						onMenuOptionSelected("EDIT", params.node?.data);
					},
					icon: '<i class="fas fa-edit"></i>',
				},
				{
					name: "New Next Down Day Order",
					action: openNewNextDownDayOrder,
					icon: '<i class="fas fa-plus"></i>',
				},
			];

			if (status === "O") {
				result.push({
					name: "Close Order",
					action: () => {
						onMenuOptionSelected("CLOSE-ORDER", params.node?.data);
					},
					icon: '<i class="far fa-times-circle"></i>',
				});
			}
			result.push(
				{
					name: "Delete Order",
					action: () => {
						onMenuOptionSelected("DELETE-ORDER", params.node?.data);
					},
					icon: '<i class="fas fa-trash"></i>',
				},
				"separator",
				{
					name: "Export to Excel",
					action: () =>
						exportTaskListRowToExcel(params.api, params.node?.data?.WorkOrderId),
					icon: '<i class="fas fa-file-excel"></i>',
				},
				{
					name: "Generate Work Order PDF",
					action: () => openWorkOrderPdf([params.node?.data?.WorkOrderId]),
					icon: '<i class="fas fa-file-pdf"></i>',
				},
				{
					name: "Export PDF List",
					action: () =>
						exportTaskListToPdf(params.api, "Next Down Day Orders", sectionAssetId),
					icon: '<i class="fas fa-file-pdf"></i>',
				},
			);
			return result;
		},
		[assetSearchId, defaultAreaId, getUserName, setNewMode, setSelectedRow, setOpenWorkOrderEditModal, onMenuOptionSelected, sectionAssetId],
	);

	const onDobleClicked1 = useCallback(() => {
		if (!gridRef1.current?.api) return;

		const selectedNodes = gridRef1.current.api.getSelectedNodes();
		if (selectedNodes.length> 0 && selectedNodes[0].data) {
			const nodeData = { ...selectedNodes[0].data };
			// Open work order: double-click jumps straight to the close action.
			onMenuOptionSelected(
				nodeData.WorkOrderStatusCode === "O" ? "CLOSE-ORDER" : "EDIT",
				nodeData,
			);
		}
	}, [onMenuOptionSelected]);

	const onDobleClicked2 = useCallback(() => {
		if (!gridRef2.current?.api) return;

		const selectedNodes = gridRef2.current.api.getSelectedNodes();
		if (selectedNodes.length> 0 && selectedNodes[0].data) {
			const nodeData = { ...selectedNodes[0].data };
			// Open work order: double-click jumps straight to the close action.
			onMenuOptionSelected(
				nodeData.WorkOrderStatusCode === "O" ? "CLOSE-ORDER" : "EDIT",
				nodeData,
			);
		}
	}, [onMenuOptionSelected]);

	const onDobleClicked3 = useCallback(() => {
		if (!gridRef3.current?.api) return;

		const selectedNodes = gridRef3.current.api.getSelectedNodes();
		if (selectedNodes.length> 0 && selectedNodes[0].data) {
			const nodeData = { ...selectedNodes[0].data };
			// Open work order: double-click jumps straight to the close action.
			onMenuOptionSelected(
				nodeData.WorkOrderStatusCode === "O" ? "CLOSE-ORDER" : "EDIT",
				nodeData,
			);
		}
	}, [onMenuOptionSelected]);

	const onDobleClicked4 = useCallback(() => {
		if (!gridRef4.current?.api) return;

		const selectedNodes = gridRef4.current.api.getSelectedNodes();
		if (selectedNodes.length> 0 && selectedNodes[0].data) {
			const nodeData = { ...selectedNodes[0].data };
			// Open work order: double-click jumps straight to the close action.
			onMenuOptionSelected(
				nodeData.WorkOrderStatusCode === "O" ? "CLOSE-ORDER" : "EDIT",
				nodeData,
			);
		}
	}, [onMenuOptionSelected]);

	const getRowId = useMemo<GetRowIdFunc>(() => {
		return (params: GetRowIdParams) => {
			return params.data?.WorkOrderId;
		};
	}, []);


	const { GetCraftFromAPI } = useGetDataFromAPI();
	useInitializeTaskList();
	useEffect(GetCraftFromAPI, []);

	const changeAsset = (value: number | null) => {
		if (value !== null && value !== assetSearchId) {
			setAssetSearchId(value);
		}
	};

	const addNewOrder = useCallback(
		async (areaAssetId: number | string, isDownTime: boolean = false) => {
			const targetAssetId = assetSearchId ?? defaultAreaId ?? 0;
			const tempWorkOrder = {
				AssetId: targetAssetId,
				AssetName: "",
				// Down Time orders default to 1 down hour so they land in Down Time
				// instead of Routine; Routine orders default to 0 down hours.
				DownHours: isDownTime ? 1 : 0,
				// Routine orders don't need hours at creation, so the field is read-only there;
				// Down Time keeps it editable.
				DownHoursReadOnly: !isDownTime,
				WorkOrderId: null,
				Title: "",
				CraftId: 1,
				TypeCode: "RM",
				WorkOrderStatusCode: "O",
				WorkOrderName: "",
				WorkOrderNumberOfWorkers: null,
				OpenTimestamp: moment(new Date()).format("MM/DD/YYYY HH:mm"),
				WorkOrderPlannedHours: null,
				WorkOrderOpenedBy: getUserName(),
			};

			setNewMode(true);
			setSelectedRow(tempWorkOrder);
			setOpenWorkOrderEditModal(true);
		},
		[
			assetSearchId,
			downHours,
			defaultAreaId,
			getUserName,
			setSelectedRow,
			setOpenWorkOrderEditModal,
		],
	);

	const handleAddDownDayPMs = useCallback(() => {
		setPickerCode("D");
	}, []);

	const handleAddNextDownDayPMs = useCallback(() => {
		setPickerCode("N");
	}, []);

	// Header "+" of the Down Day list: opens a new order already coded as Down day ('D').
	const handleAddNewDownDayOrder = useCallback(() => {
		const targetAssetId = assetSearchId ?? defaultAreaId ?? 0;
		const tempWorkOrder = {
			AssetId: targetAssetId,
			AssetName: "",
			DownHours: null,
			DownDayCode: "D",
			WorkOrderId: null,
			Title: "",
			CraftId: 1,
			TypeCode: "RM",
			WorkOrderStatusCode: "O",
			WorkOrderName: "",
			WorkOrderNumberOfWorkers: null,
			OpenTimestamp: moment(new Date()).format("MM/DD/YYYY HH:mm"),
			WorkOrderPlannedHours: null,
			WorkOrderOpenedBy: getUserName(),
		};
		setNewMode(true);
		setSelectedRow(tempWorkOrder);
		setOpenWorkOrderEditModal(true);
	}, [assetSearchId, defaultAreaId, getUserName, setSelectedRow, setOpenWorkOrderEditModal]);

	// Header "+" of the Next Down Day list: opens a new order already coded as Next down day ('N').
	const handleAddNewNextDownDayOrder = useCallback(() => {
		const targetAssetId = assetSearchId ?? defaultAreaId ?? 0;
		const tempWorkOrder = {
			AssetId: targetAssetId,
			AssetName: "",
			DownHours: null,
			DownDayCode: "N",
			WorkOrderId: null,
			Title: "",
			CraftId: 1,
			TypeCode: "RM",
			WorkOrderStatusCode: "O",
			WorkOrderName: "",
			WorkOrderNumberOfWorkers: null,
			OpenTimestamp: moment(new Date()).format("MM/DD/YYYY HH:mm"),
			WorkOrderPlannedHours: null,
			WorkOrderOpenedBy: getUserName(),
		};
		setNewMode(true);
		setSelectedRow(tempWorkOrder);
		setOpenWorkOrderEditModal(true);
	}, [assetSearchId, defaultAreaId, getUserName, setSelectedRow, setOpenWorkOrderEditModal]);

	useEffect(() => {
		if (areaApi && areaApi[1] && areaApi[2] && areaApi[3] && areaApi[4]) {
			registerDropZones(areaApi[1]);
			registerDropZones(areaApi[2]);
			registerDropZones(areaApi[3]);
			registerDropZones(areaApi[4]);
		}
	}, [areaApi, registerDropZones]);

	if (areasNumber> 0) {
		return (
			<>
				<Grid2 container
					style={{ padding: 20 }}
					onContextMenu={(e) => e.preventDefault()}>
					<Grid2 size={{ xs: 12, md: 12 }}>
						<Typography
							variant="h5"
							style={{ margin: "0", fontWeight: 600, userSelect: "none" }}>
							TASK LIST
						</Typography>
					</Grid2>
					<Paper
						elevation={1}
						style={{
							width: "100%",
							padding: 10,
							margin: "10px 0 20px",
							borderRadius: "0.75rem",
						}}>
						<Grid2 container alignItems="center" spacing={2}>
							<Grid2 size={{ xs: 12, md: 12, lg: 2 }}>
								<AssetTreePicker
									label="Asset"
									value={Number(assetSearchId ?? defaultAreaId ?? 0)}
									onChange={changeAsset} />
							</Grid2>
							<Filters />
						</Grid2>
					</Paper>
					<Grid2 container spacing={2} style={{ marginTop: "5px" }} size={{ xs: 12 }}>
						<Grid2 size={{ xs: 12, md: 4 }} style={{ height: "75vh", marginBottom: "20px" }}>
							<Paper
								elevation={2}
								style={{
									height: "75vh",
									width: "100%",
									padding: "10px 10px 0",
									borderRadius: "0.75rem",
								}}>
								<Grid2 size={{ xs: 12, md: 12 }} style={{ height: "100%", width: "100%", position: "relative", }}>
									<Grid2 size={{ xs: 12, md: 12 }}>
										<Typography
											variant="h6"
											style={{
												margin: "0 0 -30px 5px",
												fontWeight: 600,
												userSelect: "none",
											}}>
											Down Time Orders
										</Typography>
										<IconButton
											onClick={() => addNewOrder(0, true)}
											aria-label="add"
											color="primary"
											style={{
												padding: 0,
												position: "absolute",
												top: "0px",
												right: "-5px",
												zIndex: 1000,
											}}>
											<AddBoxIcon
												style={{ width: 35, height: 35, marginRight: "0" }} />
										</IconButton>
									</Grid2>
									<Grid2 style={gridStyle} id={"drop-zone-downtime"}>
										<AgGridReact
											ref={gridRef1}
											rowSelection="single"
											onRowDoubleClicked={onDobleClicked1}
											rowData={downHourRows}
											columnDefs={downHourColumnDefs}
											defaultColDef={globalDefaultColDef}
											animateRows={true}
											getContextMenuItems={getContextMenuItemsForDownHours}
											rowHeight={20}
											getRowId={getRowId}
											rowDragManaged={true}
											onRowDragEnd={onRowDragEnd}
											onGridReady={(params) => onGridReadyForArea(1, params)}
											onSelectionChanged={handleSelectionChanged}
											suppressMoveWhenRowDragging={true}
											loadingOverlayComponent={loadingOverlayComponent}
											loading={isTaskListLoading} />
									</Grid2>
									<div style={{ position: "absolute", bottom: "8px", right: "-5px", zIndex: 1000, display: "flex", alignItems: "center", gap: 8 }}>
										<PdfButton
											onClick={() => {
												const api = gridRef1.current?.api;
												if (api)
													exportTaskListToPdf(api, "Down Time Orders", sectionAssetId);
											}}
										/>
										<ExcelButton
											onClick={() => {
												const api = gridRef1.current?.api;
												if (api)
													exportTaskListToExcel(api, "TaskList - Down Time.xlsx");
											}}
										/>
									</div>
								</Grid2>
							</Paper>
						</Grid2>
						<Grid2 size={{ xs: 12, md: 4 }} style={{ height: "75vh", marginBottom: "20px" }}>
							<Paper
								elevation={2}
								style={{
									height: "75vh",
									width: "100%",
									padding: "10px 10px 10px",
									borderRadius: "0.75rem",
								}}>
								<Grid2 size={{ xs: 12, md: 12 }} style={{ height: "100%", width: "100%", display: "flex", flexDirection: "column" }}>
									{/* Next Down Day (top half): table with its generate button docked at its base */}
									<div style={{ flex: 1, minHeight: 0, position: "relative", display: "flex", flexDirection: "column" }}>
										<Typography
											variant="h6"
											style={{
												margin: "0 0 -30px 5px",
												fontWeight: 600,
												userSelect: "none",
											}}>
											Next Down Day Orders
										</Typography>
										<IconButton
											onClick={handleAddNewNextDownDayOrder}
											aria-label="add"
											color="primary"
											style={{
												padding: 0,
												position: "absolute",
												top: "0px",
												right: "-5px",
												zIndex: 1000,
											}}>
											<AddBoxIcon
												style={{ width: 35, height: 35, marginRight: "0" }} />
										</IconButton>
										<Grid2 style={{ flex: 1, minHeight: 0, width: "100%", paddingTop: "50px" }} id={"drop-zone-nextdownday"}>
											<AgGridReact
												ref={gridRef4}
												rowSelection="single"
												onRowDoubleClicked={onDobleClicked4}
												rowData={nextDownDayRows}
												columnDefs={nextDownDayColumnDefs}
												defaultColDef={globalDefaultColDef}
												animateRows={true}
												getContextMenuItems={getContextMenuItemsForNextDownDay}
												rowHeight={20}
												getRowId={getRowId}
												rowDragManaged={true}
												onRowDragEnd={onRowDragEnd}
												onGridReady={(params) => onGridReadyForArea(4, params)}
											onSelectionChanged={handleSelectionChanged}
												suppressMoveWhenRowDragging={true}
												loadingOverlayComponent={loadingOverlayComponent}
												loading={isTaskListLoading} />
										</Grid2>
										<div style={{ flex: "0 0 auto", padding: "14px 0 4px 0", marginRight: -5, minWidth: 0, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
											<Tooltip title="Generate this area's next down day orders">
												<span>
													<Button
														variant="contained"
														color="primary"
														size="small"
														sx={{
															maxWidth: "100%",
															color: "#fff",
															backgroundColor: "#a8896b",
															"&:hover": { backgroundColor: "#96785b" },
														}}
														onClick={handleAddNextDownDayPMs}>
														<span style={{ display: "inline-flex", alignItems: "center", gap: 6, lineHeight: 1 }}>
															<ScheduleIcon
																fontSize="small"
																style={{ display: "block", position: "relative", top: "-1px" }}
															/>
															Generate Next Down Day PMs
														</span>
													</Button>
												</span>
											</Tooltip>
											<div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
												<PdfButton
													onClick={() => {
														const api = gridRef4.current?.api;
														if (api)
															exportTaskListToPdf(api, "Next Down Day Orders", sectionAssetId);
													}}
												/>
												<ExcelButton
													onClick={() => {
														const api = gridRef4.current?.api;
														if (api)
															exportTaskListToExcel(api, "TaskList - Next Down Day.xlsx");
													}}
												/>
											</div>
										</div>
									</div>
									{/* Thin divider between the two down-day lists */}
									<Divider style={{ margin: "4px 0" }} />
									{/* Down Day (bottom half): table with its generate button docked at its base */}
									<div style={{ flex: 1, minHeight: 0, position: "relative", display: "flex", flexDirection: "column" }}>
										<Typography
											variant="h6"
											style={{
												margin: "0 0 -30px 5px",
												fontWeight: 600,
												userSelect: "none",
											}}>
											Down Day Orders
										</Typography>
										<IconButton
											onClick={handleAddNewDownDayOrder}
											aria-label="add"
											color="primary"
											style={{
												padding: 0,
												position: "absolute",
												top: "0px",
												right: "-5px",
												zIndex: 1000,
											}}>
											<AddBoxIcon
												style={{ width: 35, height: 35, marginRight: "0" }} />
										</IconButton>
										<Grid2 style={{ flex: 1, minHeight: 0, width: "100%", paddingTop: "50px" }} id={"drop-zone-downday"}>
											<AgGridReact
												ref={gridRef3}
												rowSelection="single"
												onRowDoubleClicked={onDobleClicked3}
												rowData={downDayRows}
												columnDefs={downDayColumnDefs}
												defaultColDef={globalDefaultColDef}
												animateRows={true}
												getContextMenuItems={getContextMenuItemsForDownDay}
												rowHeight={20}
												getRowId={getRowId}
												rowDragManaged={true}
												onRowDragEnd={onRowDragEnd}
												onGridReady={(params) => onGridReadyForArea(3, params)}
											onSelectionChanged={handleSelectionChanged}
												suppressMoveWhenRowDragging={true}
												loadingOverlayComponent={loadingOverlayComponent}
												loading={isTaskListLoading} />
										</Grid2>
										<div style={{ flex: "0 0 auto", padding: "14px 0 4px 0", marginRight: -5, minWidth: 0, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
											<Tooltip title="Generate this area's down day orders">
												<span>
													<Button
														variant="contained"
														color="primary"
														size="small"
														sx={{
															maxWidth: "100%",
															color: "#fff",
															backgroundColor: "#a8896b",
															"&:hover": { backgroundColor: "#96785b" },
														}}
														onClick={handleAddDownDayPMs}>
														<span style={{ display: "inline-flex", alignItems: "center", gap: 6, lineHeight: 1 }}>
															<ScheduleIcon
																fontSize="small"
																style={{ display: "block", position: "relative", top: "-1px" }}
															/>
															Generate Down Day PMs
														</span>
													</Button>
												</span>
											</Tooltip>
											<div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
												<PdfButton
													onClick={() => {
														const api = gridRef3.current?.api;
														if (api)
															exportTaskListToPdf(api, "Down Day Orders", sectionAssetId);
													}}
												/>
												<ExcelButton
													onClick={() => {
														const api = gridRef3.current?.api;
														if (api)
															exportTaskListToExcel(api, "TaskList - Down Day.xlsx");
													}}
												/>
											</div>
										</div>
									</div>
								</Grid2>
							</Paper>
						</Grid2>
						<Grid2 size={{ xs: 12, md: 4 }} style={{ height: "75vh", marginBottom: "20px" }}>
							<Paper
								elevation={2}
								style={{
									height: "75vh",
									width: "100%",
									padding: "10px 10px 0",
									borderRadius: "0.75rem",
								}}>
								<Grid2 size={{ xs: 12, md: 12 }} style={{ height: "100%", width: "100%", position: "relative", }}>
									<Grid2 size={{ xs: 12, md: 12 }}>
										<Typography
											variant="h6"
											style={{
												margin: "0 0 -30px 5px",
												fontWeight: 600,
												userSelect: "none",
											}}>
											Routine Orders
										</Typography>
										<IconButton
											onClick={() => addNewOrder(0, false)}
											aria-label="add"
											color="primary"
											style={{
												padding: 0,
												position: "absolute",
												top: "0px",
												right: "-5px",
												zIndex: 1000,
											}}>
											<AddBoxIcon
												style={{ width: 35, height: 35, marginRight: "0" }} />
										</IconButton>
									</Grid2>
									<Grid2 style={gridStyle} id={"drop-zone-routine"}>
										<AgGridReact
											ref={gridRef2}
											rowSelection="single"
											onRowDoubleClicked={onDobleClicked2}
											rowData={routineRows}
											columnDefs={routineColumnDefs}
											defaultColDef={globalDefaultColDef}
											animateRows={true}
											getContextMenuItems={getContextMenuItemsForRoutine}
											rowHeight={20}
											getRowId={getRowId}
											rowDragManaged={true}
											onRowDragEnd={onRowDragEnd}
											onGridReady={(params) => onGridReadyForArea(2, params)}
											onSelectionChanged={handleSelectionChanged}
											suppressMoveWhenRowDragging={true}
											loadingOverlayComponent={loadingOverlayComponent}
											loading={isTaskListLoading} />
									</Grid2>
									<div style={{ position: "absolute", bottom: "8px", right: "-5px", zIndex: 1000, display: "flex", alignItems: "center", gap: 8 }}>
										<PdfButton
											onClick={() => {
												const api = gridRef2.current?.api;
												if (api)
													exportTaskListToPdf(api, "Routine Orders", sectionAssetId);
											}}
										/>
										<ExcelButton
											onClick={() => {
												const api = gridRef2.current?.api;
												if (api)
													exportTaskListToExcel(api, "TaskList - Routine.xlsx");
											}}
										/>
									</div>
								</Grid2>
							</Paper>
						</Grid2>
					</Grid2>
				</Grid2>
				<CloseWorkOrderModalWithAttachments
					show={openCloseWorkOrderModal}
					data={selectedRow}
					onHide={(shouldUpdate: boolean) => {
						if (shouldUpdate) {
							refreshTaskList();
						}
						setOpenCloseWorkOrderModal(false);
					}} />
				<DeleteWorkOrderModal
					show={openDeleteWorkOrderModal}
					data={selectedRow}
					onHide={(shouldUpdate: boolean) => {
						if (shouldUpdate) {
							refreshTaskList();
						}
						setOpenDeleteWorkOrderModal(false);
					}} />
				{openWorkOrderEditModal && selectedRow && (
					<EditWorkOrderModal
						show={openWorkOrderEditModal}
						data={selectedRow}
						initialNewMode={newMode}
						onHide={(shouldUpdate: boolean) => {
							if (shouldUpdate) {
								refreshTaskList();
							}
							setNewMode(false);
							setOpenWorkOrderEditModal(false);
						}} />
				)}
				<DownDayPMsPicker
					show={pickerCode !== null}
					assetId={assetSearchId ?? defaultAreaId ?? null}
					downDayCode={pickerCode}
					onHide={(shouldRefresh: boolean) => {
						setPickerCode(null);
						if (shouldRefresh) {
							refreshTaskList();
						}
					}}
				/>
				<ErrorModal error={error} onHide={() => setError("")} />
				<ErrorModal error={taskListError} onHide={() => setTaskListError("")} />
				<ErrorModal
					error={insertTaskError}
					onHide={() => setInsertTaskError("")} />
				<ErrorModal
					error={updatePriorityError}
					onHide={() => setUpdatePriorityError("")} />
			</>
		);
	}
	return (
		<div
			style={{
				display: "flex",
				justifyContent: "center",
				alignItems: "center",
				height: "80vh",
			}}>
			<div className="lds-ring">
				<div></div>
				<div></div>
				<div></div>
				<div></div>
			</div>
		</div>
	);
};